import type { VoterFilter, VoterMapFilter, ShiftListParams, CanvasserType, FinanceContactListParams, ContributionListParams, ExpenditureListParams, FilingDeadlineListParams, ComplianceReportListParams, AuditLogListParams, QuestionAnswersFilter, ProspectBookQuery, Segment, TelemetryRunListParams } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { selectedCampaignKey } from './data';
import { segmentKey } from './voterSegmentKey';
import type { VoterPlaceSearch } from './voterSegmentKey';
import type { getApiClient } from '@signalvote/api-client';

/** `GET /api/admin/assistant-telemetry/overview` takes the run list's date and campaign filters. */
export type TelemetryOverviewParams = Pick<TelemetryRunListParams, 'from' | 'to' | 'campaignId'>;

export interface CanvasserAnalyticsFilters {
  type?: CanvasserType;
  vendorId?: string;
  teamId?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * A Prospects book read's filters as its key holds them: every one present, a missing one read as its default, so
 * `{}` and `{ activity: null, search: '' }` are the same read. `bookQueryOfKey` reads it back out of a key.
 */
export function bookKeyQuery(query: ProspectBookQuery): Required<ProspectBookQuery> {
  return {
    activity: query.activity ?? null,
    match: query.match ?? null,
    sheetId: query.sheetId ?? null,
    search: query.search ?? '',
    listId: query.listId ?? null,
    state: query.state ?? null,
    city: query.city ?? '',
    zip: query.zip ?? '',
    minAsk: query.minAsk ?? null,
    maxAsk: query.maxAsk ?? null,
    timeZone: query.timeZone ?? null,
    sort: query.sort ?? null,
    dir: query.dir ?? null,
  };
}

/** The filters a Prospects book key was built from, or null when the key isn't one book's read. */
export function bookQueryOfKey(key: readonly unknown[]): Required<ProspectBookQuery> | null {
  return key[3] === 'book' && typeof key[4] === 'object' && key[4] !== null ? (key[4] as Required<ProspectBookQuery>) : null;
}

// Campaign scope helper - reads current campaignId for cache isolation
function cid() {
  return useCampaignStore.getState().selectedCampaign?.id ?? '';
}

// Filter types for query keys
interface VolunteerListParams {
  page?: number;
  pageSize?: number;
}

export interface VoterListParams extends VoterFilter {
  page?: number;
  limit?: number;
}

export type TaskListParams = NonNullable<Parameters<ReturnType<typeof getApiClient>['tasks']['getTasks']>[0]>;

/**
 * The keys of the finance reads built on `defineQuery`, without the campaign id the layer puts at
 * their head. The definition and `queryKeys` both build from these, so an invalidation names the
 * key the read caches under.
 */
export const financeReadKeys = {
  health: () => ['finance', 'health'] as const,
  dashboardSummary: (window: { from: string; to: string }) =>
    ['finance', 'dashboard', 'summary', window.from, window.to] as const,
  dashboardLastFiledCash: () => ['finance', 'dashboard', 'last-filed-cash'] as const,
  dashboardRoster: () => ['finance', 'dashboard', 'roster'] as const,
  dashboardAttention: () => ['finance', 'dashboard', 'attention'] as const,
};

/**
 * Every finance query, for a write that can change any of them. A prefix key cannot do this: the
 * `defineQuery` reads hold the campaign id ahead of `'finance'`.
 */
export const financeQueries = {
  predicate: (query: { queryKey: readonly unknown[] }) =>
    query.queryKey[0] === 'finance' || query.queryKey[1] === 'finance',
};

export const queryKeys = {
  // Voters (campaign-scoped)
  voters: {
    all: ['voters'] as const,
    list: (params: VoterListParams) => ['voters', cid(), 'list', params] as const,
    detail: (id: string) => ['voters', cid(), 'detail', id] as const,
    mapPoints: (filters?: VoterMapFilter) =>
      filters
        ? (['voters', cid(), 'map-points', filters] as const)
        : (['voters', cid(), 'map-points'] as const),
    filterOptions: () => ['voters', cid(), 'filter-options'] as const,
  },

  // Volunteers (mixed scope)
  volunteers: {
    all: ['volunteers'] as const,
    list: (params?: VolunteerListParams) => ['volunteers', cid(), 'list', params] as const,
  },

  // Analytics (campaign-scoped)
  analytics: {
    all: ['analytics'] as const,
    contactAnalytics: (volunteerId?: string, vendorId?: string) =>
      ['analytics', cid(), 'contactAnalytics', volunteerId, vendorId] as const,
    campaignQuestionReport: () =>
      ['analytics', cid(), 'campaignQuestionReport'] as const,
    questionAnswers: (questionId: string, filter: QuestionAnswersFilter) =>
      ['analytics', cid(), 'questionAnswers', questionId, filter] as const,
    canvasserStats: (filters?: CanvasserAnalyticsFilters) =>
      ['analytics', cid(), 'canvasserStats', filters] as const,
    vendorSummary: (filters?: CanvasserAnalyticsFilters) =>
      ['analytics', cid(), 'vendorSummary', filters] as const,
    myStats: (filters?: Pick<CanvasserAnalyticsFilters, 'dateFrom' | 'dateTo'>) =>
      ['analytics', cid(), 'myStats', filters] as const,
    attentionNeeded: (filters?: CanvasserAnalyticsFilters, includeDismissed?: boolean) =>
      ['analytics', cid(), 'attentionNeeded', filters, { includeDismissed }] as const,
    canvasserPerformance: (userId: string) =>
      ['analytics', cid(), 'canvasserPerformance', userId] as const,
    vendorPerformance: (vendorId: string, filters?: Pick<CanvasserAnalyticsFilters, 'dateFrom' | 'dateTo'>) =>
      ['analytics', cid(), 'vendorPerformance', vendorId, filters] as const,
  },

  // Tags (campaign-scoped)
  tags: {
    all: ['tags'] as const,
    list: () => ['tags', cid(), 'list'] as const,
  },

  // Resources (campaign-scoped)
  resources: {
    all: ['resources'] as const,
    list: () => ['resources', cid(), 'list'] as const,
  },

  // Volunteer Resources (campaign-scoped)
  volunteerResources: {
    all: ['volunteerResources'] as const,
    quickTips: () => ['volunteerResources', cid(), 'quickTips'] as const,
    script: () => ['volunteerResources', cid(), 'script'] as const,
  },

  // Walkbooks (campaign-scoped)
  walkbooks: {
    all: ['walkbooks'] as const,
    list: (params?: { status?: string; assignedToMe?: boolean; includePolygon?: string }) =>
      ['walkbooks', cid(), 'list', params] as const,
    detail: (id: string) => ['walkbooks', cid(), 'detail', id] as const,
    stats: (id: string | undefined) => ['walkbooks', cid(), 'stats', id] as const,
    houses: (id: string | undefined) => ['walkbooks', cid(), 'houses', id] as const,
    contactRates: () => ['walkbooks', cid(), 'contactRates'] as const,
  },

  // Tenant Campaigns (subsidiary-scoped)
  tenantCampaigns: {
    all: ['tenant-campaigns'] as const,
    my: () => ['tenant-campaigns', 'my'] as const,
    current: () => ['tenant-campaigns', 'current'] as const,
    detail: (id: string) => ['tenant-campaigns', 'detail', id] as const,
    users: (id: string, page?: number) => ['tenant-campaigns', 'users', id, page] as const,
    invitations: (id: string) => ['tenant-campaigns', 'invitations', id] as const,
    scoutAccess: (id: string) => ['tenant-campaigns', 'scout-access', id] as const,
    consultant: (id: string) => ['tenant-campaigns', 'consultant', id] as const,
    expressConsultant: (id: string) => ['tenant-campaigns', 'express-consultant', id] as const,
  },

  playbook: {
    mine: () => ['playbook', 'mine'] as const,
  },

  // Shifts (campaign-scoped)
  shifts: {
    all: ['shifts'] as const,
    list: (params?: ShiftListParams) => ['shifts', cid(), 'list', params] as const,
    detail: (id: string) => ['shifts', cid(), 'detail', id] as const,
  },

  // Teams (campaign-scoped)
  teams: {
    all: ['teams'] as const,
    list: () => ['teams', cid(), 'list'] as const,
    detail: (id: string) => ['teams', cid(), 'detail', id] as const,
  },

  // Vendors (campaign-scoped)
  vendors: {
    all: ['vendors'] as const,
    list: () => ['vendors', cid(), 'list'] as const,
  },

  // Calendar (user-scoped, events are campaign-scoped)
  calendar: {
    all: ['calendar'] as const,
    connections: () => ['calendar', 'connections'] as const,
    calendars: (connectionId: string) => ['calendar', 'calendars', connectionId] as const,
    events: (start: string, end: string) => ['calendar', cid(), 'events', start, end] as const,
    keyDates: () => ['calendar', cid(), 'key-dates'] as const,
    campaignSettings: (campaignId: string) => ['calendar', 'campaign-settings', campaignId] as const,
    campaignVisibility: () => ['calendar', cid(), 'campaign-visibility'] as const,
  },

  // Tasks (campaign-scoped)
  tasks: {
    all: ['tasks'] as const,
    list: (params?: TaskListParams) => ['tasks', cid(), 'list', params] as const,
    detail: (id: string) => ['tasks', cid(), 'detail', id] as const,
    unacknowledgedCount: () => ['tasks', cid(), 'unacknowledged-count'] as const,
  },

  // Task Categories (campaign-scoped)
  taskCategories: {
    all: ['taskCategories'] as const,
    visible: () => ['taskCategories', cid(), 'visible'] as const,
  },

  // Campaign Questions (campaign-scoped)
  campaignQuestions: {
    all: ['campaignQuestions'] as const,
    list: () => ['campaignQuestions', cid(), 'list'] as const,
  },

  // Finance (campaign-scoped for settings, subsidiary-scoped for profiles)
  finance: {
    settings: () => ['finance', 'settings', cid()] as const,
    // Plaid bank-pull (#563) — the campaign's linked connections + their health, for the
    // Banks & processors connection-status surface. cid()-scoped for cross-campaign isolation.
    bankConnections: () => ['finance', 'bank-connections', cid()] as const,
    // Plaid bank-pull (#565) — the campaign's OPEN boundary-straggler alerts for the close-hub
    // card (GET /api/finance/straggler-alerts). cid()-scoped for cross-campaign isolation.
    stragglerAlerts: () => ['finance', 'straggler-alerts', cid()] as const,
    complianceProfile: () => ['finance', 'compliance-profile', cid()] as const,
    // ADR-069 (#616) — the platform-admin Recurring Cutoff summary (GET /api/finance/recurring).
    // cid()-scoped: recurring donations are per-committee, one campaign at a time.
    recurring: () => ['finance', 'recurring', cid()] as const,
    // ADR-069 — the per-donor recurring-donors list on the Donations page
    // (GET /api/finance/recurring/donors). cid()-scoped: one committee at a time.
    recurringDonors: () => ['finance', 'recurring', 'donors', cid()] as const,
    // ADR-076 (#627) — the per-subsidiary won/lost admin view (GET /api/finance/election-outcomes).
    // Subsidiary-scoped (NO cid()): the read spans every campaign in the subsidiary tenant DB, so
    // it's the same data whatever campaign is selected.
    electionOutcomes: () => ['finance', 'election-outcomes'] as const,
    // ADR-076 (#630) — the CURRENT campaign's per-stage outcomes backing the admin recording control
    // (GET /api/finance/election-outcomes/current). cid()-scoped: one row per stage, per campaign.
    campaignElectionOutcomes: () => ['finance', 'campaign-election-outcomes', cid()] as const,
    // FEC-009 — campaign-scoped candidate-loan list (Schedule C manual entry).
    loans: () => ['finance', 'loans', cid()] as const,
    // ADR-031 (#92) — campaign-scoped non-loan debt list (Schedule D manual entry).
    debts: () => ['finance', 'debts', cid()] as const,
    contacts: {
      all: ['finance', 'contacts'] as const,
      list: (params?: FinanceContactListParams) => ['finance', 'contacts', 'list', cid(), params] as const,
      detail: (id: string) => ['finance', 'contacts', 'detail', cid(), id] as const,
      // Mutation key for the person-vs-org shape-nudge dismiss
      // (POST /api/finance/contacts/{id}/confirm-shape). cid()-scoped + keyed by contact.
      confirmShape: (id: string) => ['finance', 'contacts', 'confirm-shape', cid(), id] as const,
    },
    contributions: {
      all: ['finance', 'contributions'] as const,
      list: (params?: ContributionListParams) => ['finance', 'contributions', 'list', cid(), params] as const,
      detail: (id: string) => ['finance', 'contributions', 'detail', cid(), id] as const,
      resolutions: (id: string) => ['finance', 'contributions', 'resolutions', cid(), id] as const,
      donorInfoRequests: (id: string) =>
        ['finance', 'contributions', 'donor-info-requests', cid(), id] as const,
    },
    expenditures: {
      all: ['finance', 'expenditures'] as const,
      list: (params?: ExpenditureListParams) => ['finance', 'expenditures', 'list', cid(), params] as const,
      detail: (id: string) => ['finance', 'expenditures', 'detail', cid(), id] as const,
    },
    deadlines: {
      all: ['finance', 'deadlines'] as const,
      list: (params?: FilingDeadlineListParams) => ['finance', 'deadlines', 'list', cid(), params] as const,
    },
    // CPH-001: Close-the-period hub key. cid()-scoped so cross-campaign
    // cache stays isolated. The /current resolver returns the open
    // FilingDeadline for the campaign — the spine surface (CPH-005), the
    // phase-state endpoint (CPH-004), and the alert band (CPH-010) all key
    // off this id.
    close: {
      all: ['finance', 'close'] as const,
      current: () => ['finance', 'close', 'current', cid()] as const,
      // USI-005 (ADR-011): the `phase-state` aggregation retired with the
      // three-phase tracker; the close hub is one page now.
    },
    // USI-007 (ADR-011 § Structural commitment #10): unified Sort Triage
    // Inbox aggregator. Replaces the per-source aggregation the deleted
    // phase-state endpoint did. cid()-scoped so cross-campaign cache
    // stays isolated.
    // Display ledger (paginated via useInfiniteQuery, ADR-026 PR-B). The optional
    // filter (Type kinds + payee/contributor search, ADR-026 PR-D) rides in a key
    // segment AFTER cid() so the 2-element sortInboxAll() invalidation prefix still
    // covers every filtered variant. An unfiltered call carries the default filter,
    // so it is deep-equal to any other default-arg call (all existing callers pass
    // no arg) — the filtered key never collides with the sortInboxQueue() key.
    sortInbox: (
      filter: { kinds: string[]; q: string } = { kinds: [], q: '' },
    ) => ['finance', 'sort-inbox', cid(), filter] as const,
    // ADR-026 (PR-A): the bounded NeedsAction work feed
    // (GET /api/finance/sort-inbox?status=needsAction), under a DISTINCT key so it
    // never shares a cache entry with the display ledger above. 'needsAction' sits
    // BEFORE cid() per the ADR-026-specified key. cid()-scoped for cross-campaign
    // isolation. Feeds TriageModal, InboxStrip's counts, and useCloseNavBadge.
    sortInboxQueue: () => ['finance', 'sort-inbox', 'needsAction', cid()] as const,
    // The "show your work" donations behind a processor's pinned fee (issue #116) —
    // GET /api/finance/processor-fees/{processorId}/donations. Lazily fetched when the
    // treasurer expands the fee row; cid()-scoped + keyed by processor.
    processorFeeDonations: (processorId: string) =>
      ['finance', 'processor-fee-donations', cid(), processorId] as const,
    // Open duplicate-contact flags for the campaign (GET
    // /api/finance/duplicate-contact-flags). Powers the inline ledger resolve
    // panel + the row "possible duplicate" badge. cid()-scoped so cross-campaign
    // cache stays isolated.
    duplicateContactFlags: () =>
      ['finance', 'duplicate-contact-flags', cid()] as const,
    // Open contact-merge OrganizationType conflicts for the campaign (GET
    // /api/finance/organization-type-conflicts, #350). Powers the inline contact-page
    // fix-it alert + the contacts-list count. cid()-scoped so cross-campaign cache
    // stays isolated.
    organizationTypeConflicts: () =>
      ['finance', 'organization-type-conflicts', cid()] as const,
    // ADR-026 (PR-A): the 2-element invalidation prefix that matches BOTH the
    // paginated display ledger (sortInbox) and the NeedsAction feed
    // (sortInboxQueue). Every finance mutation that used to invalidate the
    // 3-element sortInbox() key switches to this so neither surface goes stale;
    // for the infinite display query it refetches only the loaded pages.
    // Precisely scoped: no other finance key begins with ['finance','sort-inbox'].
    sortInboxAll: () => ['finance', 'sort-inbox'] as const,
    // CAP-009: on-demand presigned URL for a captured check image, fetched
    // when a contribution ledger row expands. cid()-scoped.
    checkImage: (contributionId: string) =>
      ['finance', 'check-image', cid(), contributionId] as const,
    reports: {
      all: ['finance', 'reports'] as const,
      list: (params?: ComplianceReportListParams) => ['finance', 'reports', 'list', cid(), params] as const,
      detail: (id: string) => ['finance', 'reports', 'detail', cid(), id] as const,
      preview: (deadlineId: string) => ['finance', 'reports', 'preview', cid(), deadlineId] as const,
      // Issue #643: warn-before-confirm ripple preview for a cash edit dated `date`.
      // cid()-scoped; staleTime=0 in the hook so the named later periods stay fresh as
      // earlier-period edits land.
      cashRecalcPreview: (date: string) =>
        ['finance', 'reports', 'cash-recalc-preview', cid(), date] as const,
      // PPR-009: pre-flight review modal. cid()-scoped so cross-campaign
      // cache stays isolated. Distinct from `preview` (FIP-008) which is
      // keyed by deadlineId, not reportId.
      filingPreview: (reportId: string) =>
        ['finance', 'reports', 'filing-preview', cid(), reportId] as const,
      // FPD-005: alert-don't-block preflight payload. cid()-scoped so
      // cross-campaign cache stays isolated; distinct key from `filingPreview`
      // since the shape (warnings + hard-failures + summary) differs.
      preflight: (reportId: string) =>
        ['finance', 'reports', 'preflight', cid(), reportId] as const,
      // Campaign-scoped prefix over every report's `preflight` (all reportIds). A ledger status
      // flip (confirm / void / mark-reported) changes the filing set, so the staleTime=0 preflight
      // card — which renders the processor-fee nudge + hard blockers — must refetch. The flip
      // mutation knows the contribution/expenditure id, not the owed report's id, so it invalidates
      // by this prefix rather than the reportId-keyed `preflight`.
      preflightPrefix: () => ['finance', 'reports', 'preflight', cid()] as const,
      // ADR-064: amendment-first / attest-then-reopen advisory for a Closed period. cid()-scoped;
      // staleTime=0 so the lead decision (amendment vs reopen) + later-closed warnings are fresh
      // each time the "change this closed period" affordance opens.
      reopenAdvisory: (reportId: string) =>
        ['finance', 'reports', 'reopen-advisory', cid(), reportId] as const,
      // FPD-013: signing-evidence DTO for the "Why is this here?" panel +
      // FPD-014's audit-packet ZIP. cid()-scoped; the response is frozen
      // post-file so a long staleTime is correct.
      signingEvidence: (reportId: string) =>
        ['finance', 'reports', 'signing-evidence', cid(), reportId] as const,
      // FPD-024: amendment-chain enumeration for the filing-version picker.
      // cid()-scoped; the chain mutates only when a new amendment files, so
      // a long staleTime is correct (panel + audit-packet UI share the cache).
      filingChain: (reportId: string) =>
        ['finance', 'reports', 'filing-chain', cid(), reportId] as const,
      // ADR-023: pre-confirm count for the always-confirm "mark filed in portal"
      // dialog. cid()-scoped; staleTime=0 so the count is fresh each dialog open.
      markFiledPreview: (reportId: string) =>
        ['finance', 'reports', 'mark-filed-preview', cid(), reportId] as const,
      // GitHub #34: per-report "Portal follow-ups" checklist. cid()-scoped so
      // cross-campaign cache stays isolated.
      portalFollowUps: (reportId: string) =>
        ['finance', 'reports', 'portal-follow-ups', cid(), reportId] as const,
      // Issue #861: the derived "Sent" lens over a report's app-send record + the pre-authorize
      // destination/county-confirmation lens. cid()-scoped so cross-campaign cache stays isolated;
      // staleTime 0 in the hook so a fresh send / delivery webhook / destination confirmation shows
      // on the authorize surface without a manual refresh.
      sendStatus: (reportId: string) =>
        ['finance', 'reports', 'send-status', cid(), reportId] as const,
      // Mississippi in-app cover review: the derived cover values, the treasurer's stored corrections,
      // and the values the cover will file. cid()-scoped so cross-campaign cache stays isolated.
      mississippiCover: (reportId: string) =>
        ['finance', 'reports', 'mississippi-cover', cid(), reportId] as const,
      // The report's latest portal-entry run (ADR-093), or with noticeOf its latest run of that row's notice.
      portalRun: (reportId: string, noticeOf: string | null = null) =>
        ['finance', 'reports', 'portal-run', cid(), reportId, noticeOf] as const,
    },
    health: () => selectedCampaignKey(financeReadKeys.health()),
    obligations: () => ['finance', 'obligations', cid()] as const,
    // GitHub #164 — VA §24.2-947.11 pre-session report preview (GET
    // /obligations/{id}/pre-session-preview). cid()-scoped so cross-campaign
    // cache stays isolated; keyed by obligationId, staleTime 0 so each modal
    // open + confirm-session-date re-scope refetches.
    preSessionPreview: (obligationId: string) =>
      ['finance', 'obligations', 'pre-session-preview', cid(), obligationId] as const,
    // GitHub #81 — AL §17-5-8(b)(3) daily-report window (GET
    // /api/finance/compliance/al-daily-window). cid()-scoped so cross-campaign
    // cache stays isolated; drives the standing in-window banner.
    alDailyWindow: () => ['finance', 'compliance', 'al-daily-window', cid()] as const,
    earliestStagedDate: () => ['finance', cid(), 'staged', 'earliest-date'] as const,
    reconciliation: {
      // USI-005 (ADR-011): the PPR-006 match-proposals, manual-match-proposal,
      // unreconciled-count, and history queries retired with the legacy
      // reconciliation backend controller + the workspace UI cluster. The
      // `all` prefix is retained because surviving classification mutations
      // still invalidate it post-classify, and the inbox in USI-007 may
      // reuse the prefix without minting a new namespace.
      all: ['finance', 'reconciliation'] as const,
    },
    // PPR-008: import-history view + Undo cascade.
    // PPR-017 adds re-attribute (mutation only; no GET cache).
    importBatches: {
      all: ['finance', 'import-batches'] as const,
      history: (limit: number) => ['finance', 'import-batches', 'history', cid(), limit] as const,
      undoPreview: (batchId: string) => ['finance', 'import-batches', 'undo-preview', cid(), batchId] as const,
    },
    // PPR-017: edit-bank-profile (future imports only).
    bankSourceProfiles: {
      all: ['finance', 'bank-source-profiles'] as const,
      detail: (profileId: string) =>
        ['finance', 'bank-source-profiles', 'detail', cid(), profileId] as const,
    },
    bankTransactions: {
      all: ['finance', 'bank-transactions'] as const,
      processorFeeCheck: (id: string) =>
        ['finance', 'bank-transactions', id, 'processor-fee-check'] as const,
      // CAP-006 — pocket-receipt suggest-match for the classify-as-expenditure
      // card. cid()-scoped so cross-campaign cache stays isolated (the
      // suggestions are campaign-scoped on the backend).
      receiptSuggestions: (id: string) =>
        ['finance', 'bank-transactions', id, 'receipt-suggestions', cid()] as const,
    },
    auditLogs: {
      all: ['finance', 'audit-logs'] as const,
      list: (params?: AuditLogListParams) => ['finance', 'audit-logs', 'list', cid(), params] as const,
      entityTypes: () => ['finance', 'audit-logs', 'entity-types', cid()] as const,
      actions: () => ['finance', 'audit-logs', 'actions', cid()] as const,
    },
    rulesets: {
      all: ['finance', 'rulesets'] as const,
      list: () => ['finance', 'rulesets', 'list'] as const,
      reportTypes: (code: string) => ['finance', 'rulesets', 'report-types', code] as const,
      // Maryland (MDCRIS, ADR-073) coded Expense Category → Purpose cascade for the expenditure-form
      // picker. Keyed by ruleset code (global reference data, not campaign-scoped); non-MD returns [].
      expensePurposes: (code: string) => ['finance', 'rulesets', 'expense-purposes', code] as const,
      // The ruleset file verbatim, for the rules viewer (global reference data).
      source: (code: string) => ['finance', 'rulesets', 'source', code] as const,
      statutoryElectionDates: (code: string, year: number) =>
        ['finance', 'rulesets', 'statutory-election-dates', code, year] as const,
    },
    // FIN-011: GET /api/finance/formats?stateCode=XX — global reference data
    // keyed by state, not campaign, because the answer is identical across
    // campaigns (mirrors RulesetController's scoping).
    formats: (stateCode: string | null) => ['finance', 'formats', stateCode] as const,
    metrics: () => ['finance', 'metrics', cid()] as const,
    // ADR-017: de-mocked dashboard hero. Summary keys on the resolved [from, to]
    // window so each period selection caches independently; last-filed cash is a
    // single per-campaign figure. cid()-scoped so cross-campaign cache stays isolated.
    dashboard: {
      summary: (from: string, to: string) => selectedCampaignKey(financeReadKeys.dashboardSummary({ from, to })),
      lastFiledCash: () => selectedCampaignKey(financeReadKeys.dashboardLastFiledCash()),
      roster: () => selectedCampaignKey(financeReadKeys.dashboardRoster()),
      attention: () => selectedCampaignKey(financeReadKeys.dashboardAttention()),
    },
    missedDeadlines: () => ['finance', 'missed-deadlines', cid()] as const,
    // PPR-002: onboarding catalog is global reference data (banks + processor
    // kinds), keyed without campaign id. Status is per-campaign and drives the
    // Setup-tab badge in FinanceLayout.
    onboardingCatalog: () => ['finance', 'onboarding', 'catalog'] as const,
    onboardingStatus: () => ['finance', 'onboarding', 'status', cid()] as const,
    // Stripe Connect committee onboarding (Finance → Payments). Per-campaign
    // connected-account health, slug, and publish state; cid()-scoped so
    // cross-campaign cache stays isolated. The account-session mint is
    // imperative (no cache key).
    payments: {
      // Prefix over every payments query (all sub-surfaces) — used to invalidate the
      // whole subtree after a write (e.g. a refund flips a gift's status + re-totals
      // the summary). cid() sits at the tail of each key, so this prefix is not
      // campaign-scoped; a sibling campaign harmlessly refetches when next viewed.
      all: () => ['finance', 'payments'] as const,
      status: () => ['finance', 'payments', 'status', cid()] as const,
      // Post-onboarding donation dashboard: DB-sourced health + donations/fees
      // this period. cid()-scoped so cross-campaign cache stays isolated.
      summary: () => ['finance', 'payments', 'summary', cid()] as const,
      // Recent-donations panel: the first page of DB-sourced portal donations.
      donations: () => ['finance', 'payments', 'donations', cid()] as const,
      // "View all" filterable feed: keyset-paginated donations, per status tab.
      donationsFeed: (tab: string) =>
        ['finance', 'payments', 'donations', 'feed', tab, cid()] as const,
      // Per-donation detail drawer: the full record for one portal donation.
      donationDetail: (id: string) =>
        ['finance', 'payments', 'donations', 'detail', id, cid()] as const,
      // Needs-attention card: open disputes/chargebacks on portal donations.
      needsAttention: () => ['finance', 'payments', 'needs-attention', cid()] as const,
      // Donation-page "Advanced settings" config (custom amount presets). cid()-scoped.
      donationConfig: () => ['finance', 'payments', 'donation-config', cid()] as const,
      // Live-only Stripe panels (never persisted, ADR-011): the connected account's
      // balance, and its recent payouts (paged via Stripe's starting_after cursor).
      balance: () => ['finance', 'payments', 'balance', cid()] as const,
      payoutSchedule: () =>
        ['finance', 'payments', 'payout-schedule', cid()] as const,
      payouts: () => ['finance', 'payments', 'payouts', cid()] as const,
    },
    // FPD-052 — configured processors with rate-shape fields. Powers the
    // settings page's processor list + per-processor rate-edit form.
    paymentProcessors: () => ['finance', 'payment-processors', cid()] as const,
    // FPD-022 — count of in-flight (Reported + null-payout) contributions per
    // processor; powers the threshold-edit row's "This affects {N}..." copy.
    processorThresholdImpact: (processorId: string) =>
      ['finance', 'payment-processors', cid(), processorId, 'threshold-impact'] as const,
    // FPD-021 — pre-fill payload for the "Mark as written off" modal launched
    // from the AgedOverdueUnsettled preflight warning.
    manualVoidPrefill: (contributionId: string) =>
      ['finance', 'contributions', cid(), contributionId, 'manual-void-prefill'] as const,
    // PPR-003: detector + training are mutation-only (no GET cache); the keys
    // exist for invalidation after train/confirm so the inbox refreshes.
    detector: ['finance', 'detector'] as const,
    // PPR-004: bank ingestion. Accounts list is campaign-scoped; commit /
    // preview / manual are mutation-only.
    bankIngestion: {
      all: ['finance', 'bank-ingestion'] as const,
      accounts: () => ['finance', 'bank-ingestion', 'accounts', cid()] as const,
    },
  },


  // Billing (campaign-scoped for status, subsidiary-scoped for config)
  billing: {
    all: ['billing'] as const,
    // Config is campaign-scoped because it surfaces the campaign's beta-pricing discount
    config: () => ['billing', cid(), 'config'] as const,
    status: () => ['billing', cid(), 'status'] as const,
    betaPricing: () => ['billing', cid(), 'beta-pricing'] as const,
    trialEmails: () => ['billing', cid(), 'trial-emails'] as const,
  },

  // Onboarding (campaign-scoped — menu + status read from the manager's real campaign)
  onboarding: {
    all: ['onboarding'] as const,
    menu: () => ['onboarding', cid(), 'menu'] as const,
    status: () => ['onboarding', cid(), 'status'] as const,
  },

  // Work Orders (campaign-scoped — internal operator surface for vendor text sends)
  workOrders: {
    all: ['work-orders'] as const,
    detail: (id: string) => ['work-orders', cid(), 'detail', id] as const,
    // Under detail, so every edit that invalidates the order also reprices its checkout.
    checkoutPreview: (id: string) => ['work-orders', cid(), 'detail', id, 'checkout-preview'] as const,
    list: (params?: { page?: number; pageSize?: number; status?: string }) =>
      ['work-orders', cid(), 'list', params ?? {}] as const,
    // The raised invoice's client secret, held so paying again reuses that invoice. Outside 'work-orders'
    // so a broad invalidation never drops it.
    checkout: (id: string) => ['work-order-checkout', cid(), id] as const,
    // Subsidiary-wide (every campaign), so deliberately not cid()-scoped.
    voterDataQueue: () => ['work-orders', 'voter-data-queue'] as const,
  },

  // Public donor donation page (UNAUTHENTICATED). Deliberately NOT cid()-scoped:
  // there is no campaign context on a public route — the subsidiary + slug in the
  // URL fully identify the page. No auth/tenant headers are ever attached.
  // A Text send's status link (UNAUTHENTICATED): the token alone identifies the send, so not cid()-scoped.
  textSendStatus: {
    byToken: (token: string) => ['textSendStatus', token] as const,
  },

  publicDonation: {
    page: (subsidiary: string, slug: string) =>
      ['publicDonation', subsidiary, slug] as const,
  },

  // Messaging Profile (campaign-scoped — 1:1 with the selected campaign)
  messagingProfile: {
    all: ['messagingProfile'] as const,
    detail: () => ['messagingProfile', cid(), 'detail'] as const,
    committee: () => ['messagingProfile', cid(), 'committee'] as const,
    // Mutation keys: which door last saved the facts tells an untick from an edit.
    committeeSaves: () => ['messagingProfile', cid(), 'committee', 'save'] as const,
    committeeSave: (door: 'facts' | 'signature') => ['messagingProfile', cid(), 'committee', 'save', door] as const,
  },
  // The platform-admin registration list spans every campaign in the subsidiary.
  messagingProfiles: {
    list: () => ['messagingProfiles', 'list'] as const,
  },

  // Voter data segment builder — live L2 counts (campaign-gated, paywall-exempt)
  voterData: {
    all: ['voterData'] as const,
    catalog: (state?: string) => ['voterData', cid(), 'catalog', state ?? null] as const,
    history: () => ['voterData', cid(), 'counts'] as const,
    counts: (segment: Segment | null) => ['voterData', cid(), 'counts', segment && segmentKey(segment)] as const,
    voteHistory: () => ['voterData', cid(), 'voteCounts'] as const,
    places: (search: VoterPlaceSearch | null) => ['voterData', cid(), 'places', search] as const,
    raceMatch: (state: string, race: string) => ['voterData', cid(), 'raceMatch', state, race] as const,
    voteCounts: (without: Segment | null, fieldId: string) =>
      ['voterData', cid(), 'voteCounts', without && segmentKey(without), fieldId] as const,
  },

  // Voter Import (mixed scope - history is campaign-scoped, records are job-scoped)
  voterImport: {
    history: (params?: { page?: number; pageSize?: number }) =>
      ['voterImport', cid(), 'history', params] as const,
    records: (jobId: string, params?: Record<string, unknown>) =>
      ['voterImport', 'records', jobId, params] as const,
    geocodingStats: () => ['voterImport', cid(), 'geocodingStats'] as const,
    stagingTable: (stagingTableName: string) =>
      ['voterImport', cid(), 'stagingTable', stagingTableName] as const,
  },

  // Fundraising / Call Time (campaign-scoped) — the Enrichment results preview reads the campaign's
  // enriched people. cid()-scoped so a campaign switch never serves the prior campaign's cache.
  fundraising: {
    all: ['fundraising'] as const,
    enrichedPeople: () => ['fundraising', cid(), 'enriched-people'] as const,
    callSheets: () => ['fundraising', cid(), 'call-sheets'] as const,
    // A book in its saved order, or in an order someone is only viewing it in — each its own read.
    callSheet: (id: string, sort: string | null = null, dir: string | null = null) =>
      ['fundraising', cid(), 'call-sheets', id, sort ?? 'saved-order', dir ?? 'saved-dir'] as const,
    enrichmentRun: () => ['fundraising', cid(), 'enrichment-run'] as const,
    followUpSettings: () => ['fundraising', cid(), 'follow-up-settings'] as const,
    openCommitments: () => ['fundraising', cid(), 'open-commitments'] as const,
    prospectLists: () => ['fundraising', cid(), 'prospect-lists'] as const,
    // Prefix of every Prospect list read, so a detail edit can patch and refresh all of them.
    prospects: () => ['fundraising', cid(), 'prospects'] as const,
    // Prefix of every Prospects book read, whatever its filters — the one read a small change must never re-ask
    // (it is the whole campaign); a change patches its rows instead.
    prospectBooks: () => ['fundraising', cid(), 'prospects', 'book'] as const,
    // Every filter the book reads is in its key, each defaulting the same whether given or left out, so two
    // reads of the same book share one entry.
    prospectBook: (query: ProspectBookQuery) =>
      ['fundraising', cid(), 'prospects', 'book', bookKeyQuery(query)] as const,
    // The book's figures without its rows. Under the Prospects prefix, so a change to a person re-asks it.
    prospectBookSummary: (query: ProspectBookQuery, withIds: boolean) =>
      ['fundraising', cid(), 'prospects', 'summary', bookKeyQuery(query), withIds] as const,
    // One Prospect's detail page. Under the Prospects prefix, so any change to a person, a verdict or a
    // call book re-asks it.
    prospectDetail: (id: string) => ['fundraising', cid(), 'prospects', 'detail', id] as const,
    // Match review's read: everyone with a candidate waiting, with the candidates. Under the Prospects prefix, so
    // any change to a person or a verdict re-asks it.
    possibleMatches: () => ['fundraising', cid(), 'prospects', 'possible-matches'] as const,
    // Under the Prospects prefix, so any change to a person or a verdict re-asks it.
    matchConfirmPreview: (prospectId: string, candidates: string[]) =>
      ['fundraising', cid(), 'prospects', 'match-preview', prospectId, ...candidates] as const,
    prospectListUndoPreview: (id: string) => ['fundraising', cid(), 'prospect-lists', id, 'undo-preview'] as const,
  },

  // Platform-admin assistant telemetry. Subsidiary-wide (no cid()): the reads span every campaign.
  assistantTelemetry: {
    all: ['assistant-telemetry'] as const,
    overview: (params: TelemetryOverviewParams) => ['assistant-telemetry', 'overview', params] as const,
    chargeQuestions: (params: TelemetryOverviewParams) => ['assistant-telemetry', 'charge-questions', params] as const,
    runs: (params: TelemetryRunListParams) => ['assistant-telemetry', 'runs', params] as const,
    run: (id: string) => ['assistant-telemetry', 'run', id] as const,
  },

  // Platform-admin Consultant management. Subsidiary-wide: users and their Express-link campaigns span every campaign.
  consultants: {
    all: ['consultants'] as const,
    list: () => ['consultants', 'list'] as const,
    search: (query: string) => ['consultants', 'search', query] as const,
  },

  // One Express link: who sent it and what a text costs through it (UNAUTHENTICATED, subsidiary-wide), and the campaigns the
  // signed-in person can send from through it.
  expressLink: {
    detail: (code: string) => ['express-link', code, 'detail'] as const,
    // Per person: a shared phone signs one out and another in without reloading the page.
    campaigns: (code: string, userId: string | undefined) => ['express-link', code, 'campaigns', userId] as const,
  },

  // The campaign's Race and its saved Vote Goals; the Race search is the public catalog, shared by every campaign.
  racePicture: {
    campaignRace: () => ['race-picture', cid(), 'campaign-race'] as const,
    search: (state: string, phrase: string) => ['race-picture', 'search', state, phrase] as const,
    race: (raceId: string) => ['race-picture', 'race', raceId] as const,
  },

  // Platform-admin Scout test bench. Subsidiary-wide; the campaign is a parameter, not the selected one.
  assistantTest: {
    all: ['assistant-test'] as const,
    campaigns: () => ['assistant-test', 'campaigns'] as const,
    members: (campaignId: string) => ['assistant-test', 'members', campaignId] as const,
  },
} as const;
